
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Events', href: '/events' },
    { name: 'News', href: '/news' },
    { 
      name: 'Programs', 
      href: '/programs',
      submenu: [
        { name: 'Farsi School', href: '/programs/farsi-school' },
        { name: 'Poetry Night', href: '/programs/poetry-night' },
        { name: 'Lecture Series', href: '/programs/lecture-series' },
        { name: 'Gisso', href: '/programs/gisso' },
        { name: 'POOIA', href: '/programs/pooia' },
      ]
    },
    { 
      name: 'About', 
      href: '/about',
      submenu: [
        { name: 'Annual Report', href: '/about/annual-report' },
        { name: 'Our People', href: '/about/our-people' },
        { name: 'Bylaws', href: '/about/bylaws' },
        { name: 'Contact', href: '/about/contact' },
      ]
    },
    { name: 'Get Involved', href: '/get-involved' },
    { name: 'Rental', href: '/rental' },
    { name: 'Shop', href: '/shop' },
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">K</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                  Kanoon
                </h1>
                <p className="text-xs text-gray-600">Persian Cultural Center</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.submenu ? (
                  <div
                    className="relative"
                    onMouseEnter={() => {
                      if (item.name === 'About') setIsAboutOpen(true);
                      if (item.name === 'Programs') setIsProgramsOpen(true);
                    }}
                    onMouseLeave={() => {
                      if (item.name === 'About') setIsAboutOpen(false);
                      if (item.name === 'Programs') setIsProgramsOpen(false);
                    }}
                  >
                    <Link
                      to={item.href}
                      className={`px-3 py-2 text-sm font-medium transition-colors flex items-center ${
                        location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                          ? 'text-red-600 border-b-2 border-red-600'
                          : 'text-gray-700 hover:text-red-600'
                      }`}
                    >
                      {item.name}
                      <i className="ri-arrow-down-s-line ml-1"></i>
                    </Link>
                    {((item.name === 'About' && isAboutOpen) || (item.name === 'Programs' && isProgramsOpen)) && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-700 hover:text-red-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/donate"
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Donate
            </Link>
            <a
              href="https://www.youtube.com/@kanoonatlanta"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
            >
              <i className="ri-youtube-line text-xl"></i>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-red-600 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`px-3 py-2 text-sm font-medium transition-colors flex items-center justify-between ${
                      location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                    {item.submenu && <i className="ri-arrow-down-s-line"></i>}
                  </Link>
                  {item.submenu && (
                    <div className="ml-4 space-y-1">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-gray-50 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center space-x-4 px-3 py-2">
                <Link
                  to="/donate"
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Donate
                </Link>
                <a
                  href="https://www.youtube.com/@kanoonatlanta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-youtube-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};